import { api } from '../lib/api.js';
import { el, fmtDate } from '../lib/ui.js';
import { icon } from '../lib/icons.js';

type ScreenTools = {
  testata: (title: string) => HTMLElement;
  screen: (...children: Array<Node | string | null | undefined>) => void;
};
type Story = {
  id: number; title: string | null; told_at: string; event_date_edtf: string | null;
  narrator?: string; snip?: string | null; has_audio: number; has_video?: number; photo: number | null;
};
type SearchResult = { storie: Story[]; persone: Array<{ id: number; display_name: string }> };
type Theme = {
  slug: string; title: string; description: string | null; emoji: string | null;
  color: string | null; count: number; cover_media_id: number | null;
};

function storyRow(m: Story): HTMLElement {
  return el('a', { class: 'storia-riga', href: `#storia/${m.id}` },
    el('div', { class: `story-visual${m.photo ? ' has-photo' : ''}`, 'aria-hidden': 'true' }, m.photo
      ? el('img', { class: 'crono-thumb', src: `/api/media/${m.photo}`, alt: '', loading: 'lazy' })
      : icon(m.has_audio ? 'mic' : 'book')),
    el('div', { class: 'story-copy' },
      el('div', { class: 'story-title' }, m.title ?? 'Racconto'),
      m.snip ? el('div', { class: 'quando' }, m.snip.replace(/\n+/g, ' ')) : null,
      el('div', { class: 'quando' },
        `${m.narrator && m.narrator !== 'mamma' ? `${m.narrator} · ` : ''}${m.event_date_edtf ? fmtDate(m.event_date_edtf) : `raccontato il ${fmtDate(m.told_at)}`}`,
        // Una storia con un video pronto lo dice, piccolo, accanto alla data.
        m.has_video ? el('span', { class: 'segno-video' }, el('span', { 'aria-hidden': 'true' }, ' · 🎞'), ' con un video') : null),
    ),
    el('span', { class: 'riga-freccia', 'aria-hidden': 'true' }, '›'),
  );
}

// La stessa ricerca è disponibile dal menu e dentro Ricordi. La query vive
// nell'URL: tornando da una storia si ritrovano le parole e i risultati.
function memorySearch(initialQuery: string, route: string, browse?: HTMLElement) {
  const input = el('input', {
    id: 'ricerca-parole', class: 'scrivi-testo', type: 'search',
    placeholder: 'Un nome, un luogo, una festa…', enterkeyhint: 'search',
    'aria-describedby': 'ricerca-stato', autocomplete: 'off',
  });
  input.value = initialQuery;
  const results = el('div', { class: 'pila ricerca-risultati' });
  const status = el('p', { id: 'ricerca-stato', class: 'data-oggi', role: 'status' });
  const submit = el('button', { type: 'submit', class: 'btn-verde' }, 'Cerca');
  const reset = el('button', { type: 'button', class: 'btn-quieto', click: () => {
    input.value = '';
    void search();
    input.focus();
  } }, 'Cancella ricerca');
  let request = 0;
  let pending: string | null = null;
  input.addEventListener('input', () => { if (!input.value.trim()) void search(); });

  async function search(): Promise<void> {
    const query = input.value.trim();
    if (pending === query) return;
    const current = ++request;
    pending = query;
    history.replaceState(history.state, '', `#${route}${query ? `?q=${encodeURIComponent(query)}` : ''}`);
    if (browse) browse.hidden = query.length > 0;
    reset.hidden = query.length === 0;
    results.replaceChildren();
    if (query.length < 2) {
      status.textContent = query ? 'Scrivi almeno due lettere.' : 'Puoi cercare anche una parola dentro un racconto.';
      results.removeAttribute('aria-busy');
      pending = null;
      return;
    }
    results.setAttribute('aria-busy', 'true');
    status.textContent = `Cerco «${query}»…`;
    try {
      const data = await api.get<SearchResult>(`/api/search?q=${encodeURIComponent(query)}`);
      if (current !== request) return;
      if (data.persone.length) results.append(el('div', { class: 'storia-persone' },
        el('span', { class: 'con-chi' }, 'Persone: '),
        ...data.persone.map((p) => el('a', { class: 'persona-chip', href: `#persona/${p.id}` }, p.display_name)),
      ));
      results.append(...data.storie.map(storyRow));
      const stories = `${data.storie.length} ${data.storie.length === 1 ? 'storia trovata' : 'storie trovate'}`;
      const people = `${data.persone.length} ${data.persone.length === 1 ? 'persona trovata' : 'persone trovate'}`;
      status.textContent = data.storie.length || data.persone.length
        ? `Per «${query}»: ${stories}${data.persone.length ? ` · ${people}` : ''}.`
        : `Nessun risultato per «${query}». Prova un nome diverso o una parola più breve.`;
    } catch (error) {
      if (current !== request || (error instanceof DOMException && error.name === 'AbortError')) return;
      status.textContent = 'Non sono riuscita a cercare. Le parole sono ancora qui: tocca Cerca per riprovare.';
    } finally {
      if (current === request) { results.removeAttribute('aria-busy'); pending = null; }
    }
  }

  const form = el('form', { class: 'ricerca-quaderno', role: 'search', submit: (event) => {
    event.preventDefault();
    return search();
  } },
    el('label', { for: 'ricerca-parole', class: 'ricerca-etichetta' }, 'Cerca nei ricordi'),
    el('div', { class: 'ricerca-controlli' }, input, submit),
    status, reset,
  );
  return { node: el('div', {}, form, results), search };
}

export async function renderSearch(ui: ScreenTools, query: string): Promise<void> {
  const search = memorySearch(query, 'cerca');
  ui.screen(ui.testata('Cerca nei ricordi'), search.node);
  await search.search();
}

export async function renderArchive(ui: ScreenTools, query: string): Promise<void> {
  const [themes, recent] = await Promise.all([
    api.get<Theme[]>('/api/themes'),
    api.get<Story[]>('/api/memories/recent?limit=4'),
  ]);
  const browse = el('div', { class: 'archivio-sfoglia' });
  const search = memorySearch(query, 'ricordi', browse);
  if (recent.length) {
    browse.append(el('section', { class: 'archive-recent', 'aria-labelledby': 'ultimi-racconti' },
      el('h2', { class: 'sezione', id: 'ultimi-racconti' }, 'Gli ultimi racconti'),
      el('div', { class: 'recent-stories' }, ...recent.map(storyRow)),
    ));
  } else if (themes.every((t) => t.count === 0)) {
    browse.append(el('div', { class: 'carta archivio-vuoto' },
      el('h2', { class: 'sezione' }, 'La prima pagina ti aspetta'),
      el('p', {}, 'Basta un piccolo ricordo. Puoi raccontarlo con la voce o scriverlo.'),
      el('a', { class: 'link-quaderno', href: '#scegli' }, 'Racconta il tuo primo ricordo →'),
    ));
  }
  // Ricordi è il posto dove si legge: da qui si arriva anche al libro, al tempo e al ricettario
  // (prima stavano solo nel Menu, a due tocchi e in un altro elenco).
  const modi = el('nav', { class: 'archivio-modi', 'aria-labelledby': 'altri-modi' },
    el('h2', { class: 'sezione', id: 'altri-modi' }, 'Sfoglia in un altro modo'),
    el('div', { class: 'menu-griglia archivio-modi-griglia' },
      ...([['book', '#libro', 'Il tuo libro', 'Le storie da leggere insieme', 11], ['clock', '#cronologia', 'La tua storia nel tempo', 'Anno dopo anno', 10], ['recipe', '#ricette', 'Il ricettario', 'Dolci e salati', 6]] as const)
        .map(([nome, href, titolo, sotto, tinta]) => el('a', { class: 'menu-voce', href, style: `--tinta:var(--tinta-${tinta})` },
          el('span', { class: 'icona' }, icon(nome)), el('span', { class: 'menu-nome' }, titolo), el('span', { class: 'sotto' }, sotto)))));
  browse.append(modi);
  const themeGrid = (items: Theme[]): HTMLElement => el('div', { class: 'griglia-temi' },
    ...items.map((t) => el('button', {
      class: 'tema-carta', ...(t.color ? { style: `--tinta:${t.color}` } : {}),
      click: () => { location.hash = `#tema/${t.slug}`; },
    },
      icon('book'),
      el('span', {}, t.title),
      t.cover_media_id ? el('img', { class: 'tema-copertina', src: `/api/media/${t.cover_media_id}?w=480`, alt: '', loading: 'lazy' }) : null,
      el('span', { class: 'conta' }, t.count ? `${t.count} ${t.count === 1 ? 'ricordo' : 'ricordi'}` : t.description ?? 'Un nuovo capitolo da raccontare'),
    )),
  );
  const filled = themes.filter((t) => t.count > 0);
  const empty = themes.filter((t) => t.count === 0);
  browse.append(el('section', { class: 'archive-chapters', 'aria-labelledby': 'capitoli-ricordi' },
    el('h2', { class: 'sezione', id: 'capitoli-ricordi' }, 'Per argomento'),
    el('p', { class: 'data-oggi' }, 'I capitoli si riempiono man mano che racconti.'),
    themeGrid(filled.length ? filled : empty),
    filled.length && empty.length ? el('details', { class: 'altri-capitoli' },
      el('summary', {}, `Altri argomenti da raccontare (${empty.length})`), themeGrid(empty),
    ) : null,
  ));
  ui.screen(ui.testata('I tuoi ricordi'), search.node, browse);
  await search.search();
}
